import { fmt, pct } from "./overviewExportCommon";

export { csvEscape, toCSV } from "./overviewExportCommon";

export function buildRpeOverviewSheets(data = {}) {
  const {
    selectedDate = "",
    selectedPosition = "All",
    selectedSessionLabel = "",
    coachRpeValue = null,
    summary = {},
    players = [],
    byPosition = [],
    distribution = [],
    sessions = [],
    trend = {},
    trendPlayers = [],
  } = data;

  const rowsSummary = [
    ["Field", "Value"],
    ["Date", selectedDate],
    ["Position filter", selectedPosition],
    ["Selected session", selectedSessionLabel],
    ["Coach RPE", fmt(coachRpeValue)],
    ["Average", fmt(summary.average)],
    ["Median", fmt(summary.median)],
    ["Min", fmt(summary.min)],
    ["Max", fmt(summary.max)],
    ["Completion rate", pct(summary.completionRate)],
    ["Answers", summary.answerCount ?? ""],
    ["Total players", summary.totalPlayers ?? ""],
    ["Coach vs team average", fmt(summary.coachVsAverageDelta)],
    ["Average delta vs previous", fmt(summary.averageDelta)],
  ];

  const rowsPlayers = [
    ["Player", "Position", "RPE", "Previous RPE", "Delta", "Response date"],
    ...players.map((row) => [
      row.name ?? "",
      row.position ?? "",
      fmt(row.rpe),
      fmt(row.previousRpe),
      fmt(row.delta),
      row.date ?? "",
    ]),
  ];

  const rowsByPosition = [
    ["Position", "Average", "Count"],
    ...byPosition.map((row) => [row.position ?? "", fmt(row.average), row.count ?? ""]),
  ];

  const rowsDistribution = [
    ["Bucket", "Count"],
    ...distribution.map((row) => [row.bucket ?? "", row.count ?? ""]),
  ];

  const rowsSessions = [
    ["Session", "Created at", "Average", "Responses", "Coach RPE", "Status"],
    ...sessions.map((session) => [
      session.label ?? "",
      session.createdAt ?? "",
      fmt(session.average),
      session.responsesCount ?? "",
      fmt(session.coachRpe),
      session.isOpen ? "open" : "closed",
    ]),
  ];

  const rowsTrendMain = [
    ["Date", "Label", "Session", "Average", "Coach RPE", "Responses"],
    ...(trend.main || []).map((row) => [
      row.date ?? "",
      row.label ?? "",
      row.sessionLabel ?? "",
      fmt(row.averageMain),
      fmt(row.coachReferenceMain),
      row.responsesCount ?? "",
    ]),
  ];

  const rowsTrendBranch = [
    ["Date", "Label", "Session", "Average", "Responses"],
    ...(trend.branch || []).map((row) => [
      row.date ?? "",
      row.label ?? "",
      row.sessionLabel ?? "",
      fmt(row.averageBranch),
      row.responsesCount ?? "",
    ]),
  ];

  const rowsTrendPlayers = [
    ["Date", "Player", "Position", "RPE"],
    ...trendPlayers.map((row) => [
      row.date ?? "",
      row.playerName ?? "",
      row.position ?? "",
      fmt(row.rpe),
    ]),
  ];

  return {
    Summary: rowsSummary,
    Players: rowsPlayers,
    ByPosition: rowsByPosition,
    Distribution: rowsDistribution,
    Sessions: rowsSessions,
    "Trend.Main": rowsTrendMain,
    "Trend.Branch": rowsTrendBranch,
    "Trend.Players": rowsTrendPlayers,
  };
}

export function buildRpeOverviewSingleCsvRows(data = {}) {
  const sheets = buildRpeOverviewSheets(data);
  const rows = [];
  const push = (...r) => rows.push(r);

  push(["dataset", "section", "field", "value"]);
  push(["meta", "summary", "date", data?.selectedDate ?? ""]);
  push(["meta", "summary", "position_filter", data?.selectedPosition ?? ""]);
  push(["meta", "summary", "selected_session", data?.selectedSessionLabel ?? ""]);
  push(["meta", "summary", "coach_rpe", fmt(data?.coachRpeValue)]);

  const summary = data?.summary || {};
  [
    ["average", summary.average],
    ["median", summary.median],
    ["min", summary.min],
    ["max", summary.max],
    ["completion_rate", summary.completionRate],
    ["answers", summary.answerCount],
    ["total_players", summary.totalPlayers],
    ["coach_vs_average", summary.coachVsAverageDelta],
    ["average_delta", summary.averageDelta],
  ].forEach(([field, value]) => {
    push(["meta", "summary", field, Number.isFinite(value) ? fmt(value) : value ?? ""]);
  });

  push([]);
  push(["Players", "name", "position", "rpe", "previous_rpe", "delta", "response_date"]);
  (data?.players || []).forEach((row) => {
    push([
      "row",
      row.name ?? "",
      row.position ?? "",
      fmt(row.rpe),
      fmt(row.previousRpe),
      fmt(row.delta),
      row.date ?? "",
    ]);
  });

  push([]);
  push(["ByPosition", "position", "average", "count"]);
  (data?.byPosition || []).forEach((row) => {
    push(["row", row.position ?? "", fmt(row.average), row.count ?? ""]);
  });

  push([]);
  push(["Distribution", "bucket", "count"]);
  (data?.distribution || []).forEach((row) => {
    push(["row", row.bucket ?? "", row.count ?? ""]);
  });

  push([]);
  push(["Sessions", "label", "created_at", "average", "responses", "coach_rpe", "status"]);
  (data?.sessions || []).forEach((session) => {
    push([
      "row",
      session.label ?? "",
      session.createdAt ?? "",
      fmt(session.average),
      session.responsesCount ?? "",
      fmt(session.coachRpe),
      session.isOpen ? "open" : "closed",
    ]);
  });

  push([]);
  push(["TrendMain", "date", "label", "session", "average", "coach_rpe", "responses"]);
  (sheets["Trend.Main"] || []).slice(1).forEach((row) => {
    push(["row", ...row]);
  });

  push([]);
  push(["TrendBranch", "date", "label", "session", "average", "responses"]);
  (sheets["Trend.Branch"] || []).slice(1).forEach((row) => {
    push(["row", ...row]);
  });

  push([]);
  push(["TrendPlayers", "date", "player", "position", "rpe"]);
  (data?.trendPlayers || []).forEach((row) => {
    push(["row", row.date ?? "", row.playerName ?? "", row.position ?? "", fmt(row.rpe)]);
  });

  return rows;
}

export function buildRpeOverviewFileBase(selectedDate) {
  const safeDate = String(selectedDate || "day").replace(/[^\d-]/g, "") || "day";
  return `rpe_overview_${safeDate}`;
}
